import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { discountCodesAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import './DiscountCodes.css';

const DiscountCodes = () => {
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, active: 0, total_redemptions: 0, total_discount_given: 0 });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: '',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({
    page: 1, limit: 10, total: 0, totalPages: 0
  });
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const filterDropdownRef = useRef(null);
  const typeDropdownRef = useRef(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchCodes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await discountCodesAPI.getAll(filters);
      if (response.data.success) {
        setCodes(response.data.data.discountCodes);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch discount codes:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = async () => {
    try {
      const response = await discountCodesAPI.getStats();
      if (response.data.success) {
        setStats(response.data.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => { fetchCodes(); }, [fetchCodes]);
  useEffect(() => { fetchStats(); }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setFilterDropdownOpen(false);
      }
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) {
        setTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }));
  };

  const handleStatusFilter = (status) => {
    setFilters(prev => ({ ...prev, status: prev.status === status ? '' : status, page: 1 }));
  };

  const handleTypeFilter = (type) => {
    setFilters(prev => ({ ...prev, type: prev.type === type ? '' : type, page: 1 }));
    setTypeDropdownOpen(false);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleDelete = async (code) => {
    try {
      await discountCodesAPI.delete(code.id);
      setDeleteConfirm(null);
      fetchCodes();
      fetchStats();
    } catch (error) {
      console.error('Failed to delete discount code:', error);
    }
  };

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'expired', label: 'Expired' }
  ];

  const typeOptions = [
    { value: '', label: 'All Types' },
    { value: 'fixed', label: 'Fixed' },
    { value: 'percentage', label: 'Percentage' },
    { value: 'capped_percentage', label: 'Capped %' }
  ];

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const formatValue = (code) => {
    if (code.type === 'fixed') return `$${parseFloat(code.value).toFixed(2)}`;
    if (code.type === 'percentage') return `${parseFloat(code.value)}%`;
    if (code.type === 'capped_percentage') return `${parseFloat(code.value)}% (max $${parseFloat(code.max_discount).toFixed(2)})`;
    return code.value;
  };

  const getCodeStatus = (code) => {
    if (!code.is_active) return { label: 'INACTIVE', className: 'status-rejected' };
    if (code.expires_at && new Date(code.expires_at) <= new Date()) return { label: 'EXPIRED', className: 'status-expired' };
    return { label: 'ACTIVE', className: 'status-approved' };
  };

  const getTypeLabel = (type) => {
    const labels = { fixed: 'FIXED', percentage: 'PERCENT', capped_percentage: 'CAPPED %' };
    return labels[type] || type;
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">DISCOUNT CODES</h1>
            <p className="page-subtitle">COUPON & PROMO CODE MANAGEMENT</p>
          </div>
          <div className="header-right">
            <div className="search-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search codes..."
                value={filters.search}
                onChange={handleSearch}
              />
            </div>
            <div className="custom-dropdown" ref={filterDropdownRef}>
              <button
                className="custom-dropdown-trigger"
                onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
              >
                <span>{statusOptions.find(opt => opt.value === filters.status)?.label || 'All Status'}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {filterDropdownOpen && (
                <div className="custom-dropdown-menu">
                  {statusOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`custom-dropdown-item ${filters.status === option.value ? 'active' : ''}`}
                      onClick={() => {
                        handleStatusFilter(option.value);
                        setFilterDropdownOpen(false);
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="custom-dropdown" ref={typeDropdownRef}>
              <button
                className="custom-dropdown-trigger"
                onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
              >
                <span>{typeOptions.find(opt => opt.value === filters.type)?.label || 'All Types'}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {typeDropdownOpen && (
                <div className="custom-dropdown-menu">
                  {typeOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`custom-dropdown-item ${filters.type === option.value ? 'active' : ''}`}
                      onClick={() => handleTypeFilter(option.value)}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button className="btn-primary" onClick={() => navigate('/discount-codes/new')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              ADD CODE
            </button>
          </div>
        </div>

        <div className="dashboard-content">
          {/* Stats Cards */}
          <div className="stats-grid stats-grid-4">
            <div
              className={`stat-card stat-card-clickable ${filters.status === '' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('')}
            >
              <div className="stat-info">
                <span className="stat-label">TOTAL CODES</span>
                <span className="stat-value">{stats.total}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-success stat-card-clickable ${filters.status === 'active' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('active')}
            >
              <div className="stat-info">
                <span className="stat-label">ACTIVE</span>
                <span className="stat-value">{stats.active}</span>
              </div>
            </div>
            <div className="stat-card stat-info-card">
              <div className="stat-info">
                <span className="stat-label">TOTAL REDEMPTIONS</span>
                <span className="stat-value">{stats.total_redemptions}</span>
              </div>
            </div>
            <div className="stat-card stat-warning">
              <div className="stat-info">
                <span className="stat-label">TOTAL DISCOUNT</span>
                <span className="stat-value">${parseFloat(stats.total_discount_given || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="dashboard-section dc-table-section">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading discount codes...</p>
              </div>
            ) : codes.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                  <line x1="7" y1="7" x2="7.01" y2="7"></line>
                </svg>
                <p>No discount codes found</p>
              </div>
            ) : (
              <>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>CODE</th>
                        <th>TYPE</th>
                        <th>VALUE</th>
                        <th>SCOPE</th>
                        <th>USAGE</th>
                        <th>STATUS</th>
                        <th>EXPIRES</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {codes.map((code) => {
                        const status = getCodeStatus(code);
                        return (
                          <tr key={code.id}>
                            <td>
                              <div className="dc-code-cell">
                                <span className="dc-code">{code.code}</span>
                                {code.description && (
                                  <span className="dc-description">{code.description}</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className={`dc-type-badge dc-type-${code.type}`}>
                                {getTypeLabel(code.type)}
                              </span>
                            </td>
                            <td>
                              <span className="dc-value">{formatValue(code)}</span>
                            </td>
                            <td>
                              <span className={`dc-scope-badge ${code.scope === 'universal' ? 'dc-scope-universal' : 'dc-scope-specific'}`}>
                                {code.scope === 'universal' ? 'ALL' : 'SPECIFIC'}
                              </span>
                            </td>
                            <td>
                              <span className="dc-usage">
                                {code.total_used}/{code.total_usage_limit || '\u221E'}
                              </span>
                            </td>
                            <td>
                              <span className={`status-badge ${status.className}`}>
                                {status.label}
                              </span>
                            </td>
                            <td>
                              <span className="dc-date">{formatDate(code.expires_at)}</span>
                            </td>
                            <td>
                              <div className="actions-cell">
                                <button
                                  className="btn-action btn-edit"
                                  onClick={() => navigate(`/discount-codes/${code.id}/edit`)}
                                >
                                  EDIT
                                </button>
                                <button
                                  className="btn-action btn-delete"
                                  onClick={() => setDeleteConfirm(code)}
                                >
                                  DELETE
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="table-footer">
                  <div className="pagination-info">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                    {pagination.total} codes
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      disabled={pagination.page === 1}
                      onClick={() => handlePageChange(pagination.page - 1)}
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`pagination-btn ${pagination.page === pageNum ? 'active' : ''}`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      className="pagination-btn"
                      disabled={pagination.page === pagination.totalPages}
                      onClick={() => handlePageChange(pagination.page + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-icon danger">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18"></path>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </div>
              <h2 className="modal-title">Delete Discount Code</h2>
              <p className="modal-subtitle">
                {deleteConfirm.total_used > 0
                  ? `Code "${deleteConfirm.code}" has ${deleteConfirm.total_used} usage(s). It will be deactivated instead.`
                  : `Are you sure you want to delete "${deleteConfirm.code}"?`
                }
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-modal-close" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn-modal-danger" onClick={() => handleDelete(deleteConfirm)}>
                {deleteConfirm.total_used > 0 ? 'Deactivate' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountCodes;
